import { useMemo } from "react";
import { Form } from "antd";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { FormInstance } from "antd/es/form/Form";
import { APP_TYPES } from "../../../constants";
import { useFetchWorkerChartOrgs } from "@lhd/hooks";

interface UseFetchDynamicOrgsProps {
  appType?: string;
  item: AppPartType;
  appParts?: AppPartType[];
  namePath: string | string[];
  form?: FormInstance;
}

export const useFetchDynamicOrgsForBusinessTrip = ({
  appType,
  item,
  appParts,
  namePath,
  form,
}: UseFetchDynamicOrgsProps) => {
  const isDepartmentField =
    appType === APP_TYPES.BUSINESS_TRIP_REPORTS &&
    item.field_code === "department_selection";

  const applicantPart = useMemo(() => {
    if (!isDepartmentField) return undefined;
    return appParts?.find(
      (ap: any) => ap.field_code === "business_trip_expense_applicant",
    );
  }, [appParts, isDepartmentField]);

  const applicantNamePath = useMemo(() => {
    if (!applicantPart) return [];
    if (Array.isArray(namePath) && namePath.length > 1) {
      return [namePath[0], String(applicantPart.id)];
    }
    return [String(applicantPart.id)];
  }, [applicantPart, namePath]);

  const applicantValue = Form.useWatch(applicantNamePath, form);

  const workerId = useMemo(() => {
    if (!applicantValue) return undefined;
    if (Array.isArray(applicantValue)) {
      return applicantValue.length > 0 ? applicantValue[0] : undefined;
    }
    return applicantValue;
  }, [applicantValue]);

  const { data: dynamicOrgs, isLoading: isOrgsLoading } =
    useFetchWorkerChartOrgs(workerId, isDepartmentField);

  const dynamicOptions = useMemo(() => {
    if (!dynamicOrgs || !Array.isArray(dynamicOrgs)) return [];
    return dynamicOrgs
      .filter((orgItem: any) => orgItem?.org)
      .map((orgItem: any) => ({
        value: String(orgItem.org.id),
        label: orgItem.org.name,
      }));
  }, [dynamicOrgs]);

  return {
    isDepartmentField,
    workerId,
    dynamicOptions,
    isOrgsLoading,
  };
};
